import React from "react";
import Tooltip from "./Tooltip";

type PaymentInputGroupProps = {
  placeholder: string;
  type?: React.HTMLInputTypeAttribute;
  name: string;
  value: string | number | readonly string[];
  onFocus?: () => void;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  toolTipText?: string;
  required?: boolean;
  disabled?: boolean;
  hideLabel?: boolean;
  maxLength?: number;
  /** Validation message shown under the field. Also marks the input invalid. */
  errorText?: string | null;
  children?: React.ReactNode;
};

const PaymentInputGroup = ({
  placeholder,
  type = "text",
  name,
  value,
  onFocus,
  onChange,
  onKeyDown,
  toolTipText,
  required = false,
  disabled = false,
  hideLabel = false,
  maxLength,
  errorText,
  children,
}: PaymentInputGroupProps) => {
  const errorId = errorText ? `${name}-error` : undefined;
  return (
    <div className="payment-input-group flex-1">
      {!hideLabel && (
        <label className="payment-label">
          {placeholder} {required && <label className="text-red-500">*</label>}
        </label>
      )}
      <div className="flex flex-row gap-4">
        {/* The field and its help affordance share a relative box so the "?"
            sits inside the input's right edge (the pattern shoppers already
            know from Shopify checkout) rather than pushing the field narrower. */}
        <div className="relative flex-1">
          <input
            type={type}
            name={name}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            className={`payment-input-field ${toolTipText ? "pr-9" : ""}`}
            required={required}
            title={toolTipText}
            disabled={disabled}
            maxLength={maxLength}
            autoComplete="off"
            aria-invalid={!!errorText}
            aria-describedby={errorId}
          />
          {toolTipText && (
            <span className="absolute right-2 top-1/2 -translate-y-1/2 flex">
              <Tooltip tooltip={toolTipText} position="left" multiline>
                <button
                  type="button"
                  // Explanatory only — it must never submit the checkout form
                  // or steal the field's value on click.
                  onClick={(e) => e.preventDefault()}
                  aria-label={`Why we ask for ${placeholder}: ${toolTipText}`}
                  className="flex h-5 w-5 items-center justify-center rounded-full border border-gray-400 text-[11px] font-semibold leading-none text-gray-500 hover:border-gray-600 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  ?
                </button>
              </Tooltip>
            </span>
          )}
        </div>
        {children}
      </div>
      {errorText && (
        <p id={errorId} className="text-red-500 text-xs mt-1" role="alert">
          {errorText}
        </p>
      )}
    </div>
  );
};

export default PaymentInputGroup;
